import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UploadCloud, Loader2, Users, Database, Search, X, Building2, ChevronLeft, ChevronRight,
  ArrowUpRight, Filter, FileText, RefreshCw, Trash2, Zap, ScanLine,
} from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";
import { BatchCardScanner } from "@/components/BatchCardScanner";

const PAGE_SIZES = [25, 50, 100];

function StatTile({ label, value, tone = "green", testId }) {
  const cls = { green: "bg-[#083F22] text-[#E4D3A0]", gold: "bg-[#C9A24B] text-[#0F1E15]", ivory: "bg-[#F1EDDF] text-[#0F1E15]" }[tone];
  return (
    <div className={`rounded-2xl overflow-hidden border border-[#0F5E39]/15 ${cls} p-5`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">{label}</div>
      <div className="font-serif text-3xl font-medium mt-1">{value}</div>
    </div>
  );
}

// ==================================================================
// IMPORT DIALOG
// ==================================================================
function ImportPanel({ onClose, onImported }) {
  const [phase, setPhase] = useState("idle"); // idle | uploading | tracking | done | failed
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const csvRef = useRef(null);
  const vcfRef = useRef(null);

  const upload = async (endpoint, file) => {
    setErr("");
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJobId(data.job_id);
      setPhase("tracking");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
      setPhase("failed");
    }
  };

  useEffect(() => {
    if (phase !== "tracking" || !jobId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/super-admin/master-contacts/import/${jobId}`);
        setJob(data);
        if (data.status === "done") {
          setPhase("done");
          toast.success(`Imported ${data.rows_inserted} new / ${data.rows_updated} updated`);
          onImported?.();
        } else if (data.status === "failed") {
          setPhase("failed");
          setErr(data.error || "Import failed");
        } else if (!stopped) {
          setTimeout(tick, 1500);
        }
      } catch (_) {
        if (!stopped) setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { stopped = true; };
  }, [phase, jobId, onImported]);

  const rowsRead = job?.rows_read || 0;
  const rowsInserted = job?.rows_inserted || 0;
  const rowsUpdated = job?.rows_updated || 0;
  const rowsSkipped = job?.rows_skipped || 0;

  return (
    <div className="space-y-4" data-testid="import-panel">
      {phase === "idle" && (
        <>
          <div className="rounded-2xl border-2 border-dashed border-[#0F5E39]/30 bg-[#F6F1E1]/40 p-8 text-center">
            <UploadCloud size={40} className="mx-auto text-[#0F5E39]" strokeWidth={1.5}/>
            <div className="font-serif text-2xl mt-3">Upload the cleaned CSV</div>
            <p className="text-sm text-[#4A5A50] mt-1 max-w-md mx-auto">
              Streamed, batched, deduped by phone or GSTIN. Handles files up to ~176k rows.
              Mobiles auto-normalised to +91.
            </p>
            <input
              ref={csvRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && upload("/super-admin/master-contacts/import/csv", e.target.files[0])}
              data-testid="master-csv-input"
            />
            <Button
              className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-12 px-8 mt-5"
              onClick={() => csvRef.current?.click()}
              data-testid="master-upload-csv-btn"
            >
              <UploadCloud size={18} className="mr-2"/> Choose CSV file
            </Button>
          </div>

          <div className="rounded-2xl border border-[#E4DFCF] p-6 text-center">
            <div className="text-sm text-[#4A5A50]">…or import a phone contacts backup</div>
            <input
              ref={vcfRef}
              type="file"
              accept=".vcf,text/vcard"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && upload("/super-admin/master-contacts/import/vcf", e.target.files[0])}
              data-testid="master-vcf-input"
            />
            <Button
              variant="outline"
              className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 mt-3 h-11 px-6"
              onClick={() => vcfRef.current?.click()}
              data-testid="master-upload-vcf-btn"
            >
              <FileText size={16} className="mr-2"/> Upload vCard (.vcf)
            </Button>
          </div>
        </>
      )}

      {(phase === "uploading" || phase === "tracking") && (
        <div className="rounded-2xl border border-[#E4DFCF] bg-white p-8 text-center space-y-3">
          <Loader2 className="animate-spin text-[#0F5E39] mx-auto" size={40}/>
          <div className="font-serif text-2xl">Importing…</div>
          <p className="text-sm text-[#4A5A50]">You can safely close this window — import continues in the background.</p>
          <div className="grid grid-cols-4 gap-3 mt-4 max-w-md mx-auto text-left">
            <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Read</div><div className="font-serif text-xl text-[#0F5E39]">{rowsRead}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">New</div><div className="font-serif text-xl text-[#0F5E39]">{rowsInserted}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Updated</div><div className="font-serif text-xl text-[#C9A24B]">{rowsUpdated}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Skipped</div><div className="font-serif text-xl text-[#4A5A50]">{rowsSkipped}</div></div>
          </div>
        </div>
      )}

      {phase === "done" && job && (
        <div className="rounded-2xl border border-[#0F5E39]/30 bg-[#E1F0E6] p-8 text-center space-y-2">
          <div className="font-serif text-2xl text-[#0F5E39]">Import complete</div>
          <p className="text-sm text-[#0F5E39]">
            {job.rows_inserted} new · {job.rows_updated} updated · {job.rows_skipped} skipped · {job.rows_read} rows read
          </p>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white mt-3" onClick={onClose} data-testid="import-done-close">
            View contacts
          </Button>
        </div>
      )}

      {phase === "failed" && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
          <div className="font-medium text-red-700">Import failed</div>
          <div className="text-sm text-red-600 mt-1">{err || "Unknown error"}</div>
          <Button variant="outline" className="rounded-full mt-3" onClick={() => { setPhase("idle"); setErr(""); setJob(null); setJobId(null); }}>Try again</Button>
        </div>
      )}
    </div>
  );
}


// ==================================================================
// PROMOTE DIALOG
// ==================================================================
function PromoteDialog({ open, onOpenChange, selectedIds, currentFilter, resultCount, onDone }) {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [typeOverride, setTypeOverride] = useState("");
  const [busy, setBusy] = useState(false);
  const [usePayload, setUsePayload] = useState(selectedIds.length ? "ids" : "filter");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/overview");
        setCompanies((data.companies || []).filter((c) => c.code !== "SUPER"));
      } catch (_) { /* silent */ }
    })();
    setUsePayload(selectedIds.length ? "ids" : "filter");
  }, [open, selectedIds.length]);

  useEffect(() => {
    if (!companyId) { setUsers([]); return; }
    (async () => {
      try {
        // Impersonate briefly via super-admin to fetch users of that company
        await api.post("/super-admin/switch-tenant", { company_id: companyId });
        const { data } = await api.get("/users");
        setUsers(data || []);
      } catch (_) { setUsers([]); }
    })();
  }, [companyId]);

  const submit = async () => {
    if (!companyId) return toast.error("Choose a company");
    setBusy(true);
    try {
      const body = {
        company_id: companyId,
        assigned_to: assignedTo || null,
        contact_type_override: typeOverride || null,
      };
      if (usePayload === "ids") body.ids = selectedIds;
      else body.filter = currentFilter;
      const { data } = await api.post("/super-admin/master-contacts/promote", body);
      toast.success(`Promoted ${data.promoted} contacts · ${data.duplicates_skipped} duplicates skipped`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Promote failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="promote-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Promote to company CRM</DialogTitle>
          <DialogDescription>
            Contacts stay in the master pool. A copy is created in the chosen company&apos;s operational CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Scope</Label>
            <div className="mt-2 flex flex-col gap-2">
              <label className={`rounded-xl border p-3 cursor-pointer ${usePayload === "ids" ? "border-[#0F5E39] bg-[#E1F0E6]/40" : "border-[#E4DFCF]"}`}>
                <input type="radio" checked={usePayload === "ids"} onChange={() => setUsePayload("ids")} disabled={!selectedIds.length} className="mr-2"/>
                <span className="font-medium">Selected rows</span>
                <span className="text-sm text-[#4A5A50] ml-2">{selectedIds.length} chosen</span>
              </label>
              <label className={`rounded-xl border p-3 cursor-pointer ${usePayload === "filter" ? "border-[#0F5E39] bg-[#E1F0E6]/40" : "border-[#E4DFCF]"}`}>
                <input type="radio" checked={usePayload === "filter"} onChange={() => setUsePayload("filter")} className="mr-2"/>
                <span className="font-medium">Current filter segment</span>
                <span className="text-sm text-[#4A5A50] ml-2">{resultCount} matched</span>
              </label>
            </div>
          </div>

          <div>
            <Label>Target company</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="mt-1" data-testid="promote-company-select"><SelectValue placeholder="Choose a tenant"/></SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Assign to salesperson (optional)</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger className="mt-1" data-testid="promote-user-select"><SelectValue placeholder="Unassigned"/></SelectTrigger>
              <SelectContent>
                {users
                  .filter((u) => ["sales", "order_team", "manager", "admin"].includes(u.role))
                  .map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Contact type override (optional)</Label>
            <Select value={typeOverride} onValueChange={setTypeOverride}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Keep source type"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={submit} disabled={busy} data-testid="promote-submit-btn">
            {busy ? <Loader2 className="animate-spin mr-2" size={14}/> : <ArrowUpRight size={14} className="mr-1"/>}
            Promote to CRM
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================================================================
// MAIN PAGE
// ==================================================================
export default function MasterContactsPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [type, setType] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [hasEmail, setHasEmail] = useState("");
  const [hasGst, setHasGst] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showBatchScan, setShowBatchScan] = useState(false);
  const [showPromote, setShowPromote] = useState(false);

  const filter = useMemo(() => ({
    q: qDebounced || undefined,
    state: state || undefined,
    city: city || undefined,
    type: type || undefined,
    business_type: businessType || undefined,
    has_email: hasEmail === "" ? undefined : hasEmail === "yes",
    has_gst: hasGst === "" ? undefined : hasGst === "yes",
  }), [qDebounced, state, city, type, businessType, hasEmail, hasGst]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/super-admin/master-contacts/stats");
      setStats(data);
    } catch (_) { /* silent */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...filter, page, page_size: pageSize };
      Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
      const { data } = await api.get("/super-admin/master-contacts", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); }, [page, pageSize, filter]);

  useEffect(() => { setPage(1); }, [filter]);

  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      const allOnPage = rows.every((r) => s.has(r.id));
      const n = new Set(s);
      if (allOnPage) rows.forEach((r) => n.delete(r.id));
      else rows.forEach((r) => n.add(r.id));
      return n;
    });
  };

  const clearFilters = () => {
    setQ(""); setState(""); setCity(""); setType(""); setBusinessType(""); setHasEmail(""); setHasGst("");
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6" data-testid="master-contacts-page">
      <PageHead
        eyebrow="TARU Platform"
        title="Master Contacts"
        subtitle="Platform-level lead pool. Super Admin only. Not visible to any tenant CRM."
        actions={
          <>
            <Button variant="outline" className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 h-11" onClick={() => nav("/super-admin/promotion-rules")} data-testid="master-automations-btn">
              <Zap size={14} className="mr-1"/> Automations
            </Button>
            <Button variant="outline" className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 h-11" onClick={() => setShowBatchScan(true)} data-testid="master-scan-cards-btn">
              <ScanLine size={14} className="mr-1"/> Scan cards
            </Button>
            <Button variant="outline" className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 h-11" onClick={() => { loadStats(); load(); }} data-testid="master-refresh-btn">
              <RefreshCw size={14} className="mr-1"/> Refresh
            </Button>
            <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-11 px-6" onClick={() => setShowImport(true)} data-testid="master-import-btn">
              <UploadCloud size={16} className="mr-2"/> Import
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatTile label="Total"     value={stats?.total ?? "—"}      tone="green" testId="stat-total"/>
        <StatTile label="Customers" value={stats?.customers ?? "—"}  tone="ivory" testId="stat-customers"/>
        <StatTile label="Leads"     value={stats?.leads ?? "—"}      tone="ivory" testId="stat-leads"/>
        <StatTile label="With Email" value={stats?.with_email ?? "—"} tone="gold" testId="stat-with-email"/>
        <StatTile label="With GST"  value={stats?.with_gst ?? "—"}    tone="gold" testId="stat-with-gst"/>
      </div>

      {/* Filters */}
      <Card className="border-[#E4DFCF] rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Search</Label>
              <div className="relative mt-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, mobile, email, GSTIN…" className="pl-9 h-11 rounded-full bg-white" data-testid="master-search-input"/>
              </div>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">State</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. Gujarat" className="mt-1 h-11 rounded-full bg-white w-40"/>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Surat" className="mt-1 h-11 rounded-full bg-white w-40"/>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1 h-11 rounded-full w-32"><SelectValue placeholder="Any"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Customer">Customer</SelectItem>
                  <SelectItem value="Lead">Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Business</Label>
              <Input value={businessType} onChange={(e) => setBusinessType(e.target.value)} placeholder="Retailer / Wholesaler" className="mt-1 h-11 rounded-full bg-white w-44"/>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Has email</Label>
              <Select value={hasEmail} onValueChange={setHasEmail}>
                <SelectTrigger className="mt-1 h-11 rounded-full w-28"><SelectValue placeholder="Any"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Has GST</Label>
              <Select value={hasGst} onValueChange={setHasGst}>
                <SelectTrigger className="mt-1 h-11 rounded-full w-28"><SelectValue placeholder="Any"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="rounded-full h-11" onClick={clearFilters}><X size={14} className="mr-1"/>Reset</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table + actions */}
      <SectionHead
        title={`Contacts (${total.toLocaleString("en-IN")})`}
        subtitle={selected.size ? `${selected.size} selected` : "Click a row to select. Filter, then promote a whole segment."}
        actions={
          <Button
            className="rounded-full bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15] h-11 px-6"
            disabled={total === 0}
            onClick={() => setShowPromote(true)}
            data-testid="promote-open-btn"
          >
            <ArrowUpRight size={16} className="mr-2"/> Promote to CRM
          </Button>
        }
      />

      {total === 0 && !loading ? (
        <EmptyState
          icon={Database}
          eyebrow="Master pool is empty"
          title="Upload your first list"
          description="Import the cleaned CSV to seed the platform lead database. Contacts here NEVER show up in any tenant CRM until you promote them."
          action={{ label: "Import CSV", onClick: () => setShowImport(true), testId: "empty-import-btn" }}
        />
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
                    <th className="p-3 text-left w-10">
                      <Checkbox
                        checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                        onCheckedChange={toggleAll}
                        data-testid="select-all"
                      />
                    </th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Mobile</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">City / State</th>
                    <th className="p-3 text-left">Business</th>
                    <th className="p-3 text-left">GSTIN</th>
                    <th className="p-3 text-left">Promoted</th>
                  </tr>
                </thead>
                <tbody className={loading ? "opacity-50" : ""}>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[#E4DFCF] hover:bg-[#F6F1E1]/40" data-testid={`master-row-${r.id}`}>
                      <td className="p-3 align-top">
                        <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid={`row-check-${r.id}`}/>
                      </td>
                      <td className="p-3 align-top">
                        <div className="font-medium">{r.company_name || "—"}</div>
                        {r.customer_code && <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{r.customer_code}</div>}
                      </td>
                      <td className="p-3 align-top">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${r.type === "Customer" ? "bg-[#E1F0E6] text-[#0F5E39]" : "bg-[#F6EED8] text-[#6E5416]"}`}>
                          {r.type || "—"}
                        </span>
                      </td>
                      <td className="p-3 align-top font-mono text-[13px]">{r.primary_mobile || "—"}</td>
                      <td className="p-3 align-top truncate max-w-[200px]">{r.primary_email || "—"}</td>
                      <td className="p-3 align-top">
                        <div>{r.city || "—"}</div>
                        <div className="text-xs text-[#4A5A50]">{r.state || ""}</div>
                      </td>
                      <td className="p-3 align-top">{r.business_type || "—"}</td>
                      <td className="p-3 align-top font-mono text-[13px]">{r.gstin || "—"}</td>
                      <td className="p-3 align-top">
                        {(r.promoted_to || []).length
                          ? <span className="text-[#0F5E39] text-xs">✓ {r.promoted_to.length}×</span>
                          : <span className="text-[#4A5A50] text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-[#E4DFCF] bg-[#F6F1E1]/40">
              <div className="text-xs text-[#4A5A50]">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString("en-IN")}
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="h-9 rounded-full w-24 text-xs" data-testid="master-page-size"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s} / page</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="rounded-full h-9" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="master-prev-page"><ChevronLeft size={14}/></Button>
                <div className="text-xs">Page {page} of {totalPages}</div>
                <Button variant="outline" size="sm" className="rounded-full h-9" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="master-next-page"><ChevronRight size={14}/></Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-xl bg-white" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Import Master Contacts</DialogTitle>
            <DialogDescription>CSV (recommended) or vCard (.vcf). Dedupe by phone or GSTIN. Runs in background.</DialogDescription>
          </DialogHeader>
          <ImportPanel onClose={() => { setShowImport(false); loadStats(); load(); }} onImported={() => { loadStats(); load(); }}/>
        </DialogContent>
      </Dialog>

      {/* Batch card scan dialog */}
      <Dialog open={showBatchScan} onOpenChange={setShowBatchScan}>
        <DialogContent className="max-w-4xl bg-white max-h-[92vh] overflow-y-auto" data-testid="batch-scan-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Scan visiting cards into master pool</DialogTitle>
            <DialogDescription>
              Upload up to 20 card photos. AI extracts each; you verify + save. Deduped against the master pool by phone.
            </DialogDescription>
          </DialogHeader>
          <BatchCardScanner mode="master" onDone={() => { loadStats(); load(); }}/>
        </DialogContent>
      </Dialog>

      {/* Promote dialog */}
      <PromoteDialog
        open={showPromote}
        onOpenChange={setShowPromote}
        selectedIds={Array.from(selected)}
        currentFilter={filter}
        resultCount={total}
        onDone={() => { setSelected(new Set()); loadStats(); load(); }}
      />
    </div>
  );
}
